import type { BusyOnlyCalendarProjection, FullDetailCalendarProjection } from '@vivport/backend/types';
import { useMemo } from 'react';
import { CalendarBusyOverlay } from './CalendarBusyOverlay';
import { CalendarEventCard } from './CalendarEventCard';

const DAY_COUNT = 7;
const HOUR_COUNT = 24;
const HOUR_HEIGHT = 56;
const GRID_HEIGHT = HOUR_COUNT * HOUR_HEIGHT;
const TIME_GUTTER_WIDTH = 72;
const MIN_BLOCK_HEIGHT = 20;
const DAY_MS = 24 * 60 * 60 * 1000;

type TimedSegment<T> = {
  item: T;
  dayIndex: number;
  startMinute: number;
  endMinute: number;
  top: number;
  height: number;
  segmentStart: Date;
  segmentEnd: Date;
};

type PositionedDetailSegment = TimedSegment<FullDetailCalendarProjection> & {
  lane: number;
  laneCount: number;
};

interface CalendarGridProps {
  weekStart: Date;
  fullDetailEvents: FullDetailCalendarProjection[];
  busyOnlyOverlays: BusyOnlyCalendarProjection[];
}

function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function addDays(value: Date, days: number): Date {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
}

function formatDayLabel(value: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(value);
}

function formatHourLabel(hour: number): string {
  const value = new Date();
  value.setHours(hour, 0, 0, 0);
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).format(value);
}

function minutesBetween(start: Date, end: Date): number {
  return (end.getTime() - start.getTime()) / (60 * 1000);
}

function toTimedSegments<T extends { start: string; end: string }>(
  item: T,
  weekStart: Date
): Array<TimedSegment<T>> {
  const start = new Date(item.start);
  const endRaw = new Date(item.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(endRaw.getTime())) {
    return [];
  }
  const end = endRaw.getTime() > start.getTime() ? endRaw : new Date(start.getTime() + 30 * 60 * 1000);
  const weekStartDay = startOfDay(weekStart);
  const weekEndDay = new Date(weekStartDay.getTime() + DAY_COUNT * DAY_MS);
  if (end <= weekStartDay || start >= weekEndDay) {
    return [];
  }

  const segments: Array<TimedSegment<T>> = [];
  for (let dayIndex = 0; dayIndex < DAY_COUNT; dayIndex += 1) {
    const dayStart = addDays(weekStartDay, dayIndex);
    const dayEnd = new Date(dayStart.getTime() + DAY_MS);

    const segmentStart = start.getTime() > dayStart.getTime() ? start : dayStart;
    const segmentEnd = end.getTime() < dayEnd.getTime() ? end : dayEnd;
    if (segmentEnd <= segmentStart) {
      continue;
    }

    const startMinute = Math.max(0, minutesBetween(dayStart, segmentStart));
    const endMinute = Math.min(24 * 60, minutesBetween(dayStart, segmentEnd));
    const top = (startMinute / 60) * HOUR_HEIGHT;
    const height = Math.max(((endMinute - startMinute) / 60) * HOUR_HEIGHT, MIN_BLOCK_HEIGHT);
    segments.push({
      item,
      dayIndex,
      startMinute,
      endMinute,
      top,
      height,
      segmentStart,
      segmentEnd,
    });
  }

  return segments;
}

function assignDetailLanes(segments: Array<TimedSegment<FullDetailCalendarProjection>>): PositionedDetailSegment[] {
  const byDay = new Map<number, Array<TimedSegment<FullDetailCalendarProjection>>>();
  for (const segment of segments) {
    const list = byDay.get(segment.dayIndex);
    if (list) {
      list.push(segment);
    } else {
      byDay.set(segment.dayIndex, [segment]);
    }
  }

  const positioned: PositionedDetailSegment[] = [];
  for (const [, daySegments] of byDay) {
    daySegments.sort((a, b) => {
      if (a.startMinute === b.startMinute) {
        return b.endMinute - a.endMinute;
      }
      return a.startMinute - b.startMinute;
    });

    const laneEnds: number[] = [];
    const withLane: Array<PositionedDetailSegment> = [];
    for (const segment of daySegments) {
      let lane = laneEnds.findIndex((laneEnd) => laneEnd <= segment.startMinute);
      if (lane === -1) {
        lane = laneEnds.length;
      }
      laneEnds[lane] = segment.endMinute;
      withLane.push({
        ...segment,
        lane,
        laneCount: 0,
      });
    }

    const laneCount = Math.max(1, laneEnds.length);
    for (const segment of withLane) {
      positioned.push({
        ...segment,
        laneCount,
      });
    }
  }

  return positioned;
}

export function CalendarGrid({ weekStart, fullDetailEvents, busyOnlyOverlays }: CalendarGridProps) {
  const weekDays = useMemo(
    () => Array.from({ length: DAY_COUNT }, (_, index) => addDays(startOfDay(weekStart), index)),
    [weekStart]
  );

  const busySegments = useMemo(() => busyOnlyOverlays.flatMap((item) => toTimedSegments(item, weekStart)), [busyOnlyOverlays, weekStart]);

  const positionedDetailSegments = useMemo(
    () => assignDetailLanes(fullDetailEvents.flatMap((item) => toTimedSegments(item, weekStart))),
    [fullDetailEvents, weekStart]
  );

  return (
    <div className="overflow-x-auto rounded-md border bg-background">
      <div className="grid min-w-[1080px]" style={{ gridTemplateColumns: `${TIME_GUTTER_WIDTH}px repeat(${DAY_COUNT}, minmax(140px, 1fr))` }}>
        <div className="border-b bg-muted/40" />
        {weekDays.map((day) => (
          <div key={day.toISOString()} className="border-b border-l bg-muted/40 px-2 py-2 text-xs font-medium">
            {formatDayLabel(day)}
          </div>
        ))}

        <div className="relative border-r" style={{ height: GRID_HEIGHT }}>
          {Array.from({ length: HOUR_COUNT + 1 }, (_, hour) => (
            <div key={hour} className="absolute left-0 right-0 border-t border-border/60" style={{ top: hour * HOUR_HEIGHT }}>
              {hour < HOUR_COUNT ? (
                <span className="-translate-y-1/2 px-2 text-[10px] text-muted-foreground">{formatHourLabel(hour)}</span>
              ) : null}
            </div>
          ))}
        </div>

        {weekDays.map((day, dayIndex) => (
          <div key={`${day.toISOString()}-column`} className="relative border-l" style={{ height: GRID_HEIGHT }}>
            {Array.from({ length: HOUR_COUNT + 1 }, (_, hour) => (
              <div key={hour} className="absolute left-0 right-0 border-t border-border/60" style={{ top: hour * HOUR_HEIGHT }} />
            ))}

            {busySegments
              .filter((segment) => segment.dayIndex === dayIndex)
              .map((segment, index) => (
                <CalendarBusyOverlay
                  key={`${dayIndex}-busy-${index}-${segment.segmentStart.toISOString()}`}
                  style={{
                    top: segment.top,
                    height: segment.height,
                    left: 4,
                    right: 4,
                  }}
                />
              ))}

            {positionedDetailSegments
              .filter((segment) => segment.dayIndex === dayIndex)
              .map((segment) => {
                const width = `calc(${100 / segment.laneCount}% - 6px)`;
                const left = `calc(${(100 / segment.laneCount) * segment.lane}% + 3px)`;
                return (
                  <CalendarEventCard
                    key={`${segment.item.eventId}-${segment.segmentStart.toISOString()}`}
                    event={segment.item}
                    segmentStart={segment.segmentStart}
                    segmentEnd={segment.segmentEnd}
                    style={{
                      top: segment.top,
                      height: segment.height,
                      width,
                      left,
                    }}
                  />
                );
              })}
          </div>
        ))}
      </div>
    </div>
  );
}
